<template>

  <section
    class="password-page"
    :class="{ disabled: isDisabled }"
  >

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <div class="password-card">

      <div class="card-glow card-glow-blue"></div>
      <div class="card-glow card-glow-purple"></div>


      <div class="card-header">

        <div class="header-left">

          <div class="header-icon">

            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M8.5 11V8.5C8.5 6.57 10.07 5 12 5C13.93 5 15.5 6.57 15.5 8.5V11"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

              <rect
                x="6"
                y="11"
                width="12"
                height="9"
                rx="2.5"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M12 14.5V17"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />

            </svg>

          </div>


          <div class="header-copy">

            <div class="eyebrow">
              Безопасность
            </div>

            <h2 class="card-title">
              Смена пароля
            </h2>

            <p class="card-description">
              Используйте надёжный пароль, который вы не применяете
              в других сервисах.
            </p>

          </div>

        </div>


        <div
          v-if="isDisabled"
          class="availability-badge"
        >
          <span class="availability-dot"></span>

          Скоро
        </div>

      </div>


      <!-- =====================================================
           DISABLED NOTICE
      ====================================================== -->

      <div
        v-if="isDisabled"
        class="disabled-notice"
      >

        <div class="disabled-notice-icon">

          <svg
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              cx="12"
              cy="12"
              r="8"
              stroke="currentColor"
              stroke-width="1.6"
            />

            <path
              d="M12 8V12.5"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <circle
              cx="12"
              cy="15.8"
              r="1"
              fill="currentColor"
            />

          </svg>

        </div>


        <div>

          <div class="disabled-notice-title">
            Смена пароля временно недоступна
          </div>

          <div class="disabled-notice-description">
            Мы работаем над этой функцией. Управление паролем
            появится в одном из следующих обновлений.
          </div>

        </div>

      </div>

    </div>


    <!-- =====================================================
         PASSWORD FORM
    ====================================================== -->

    <div class="form-card">

      <div class="section-heading">

        <div class="section-eyebrow">
          Новый пароль
        </div>

        <h3>
          Обновите данные для входа
        </h3>

        <p>
          Новый пароль должен содержать не менее 8 символов.
        </p>

      </div>


      <div class="fields">

        <!-- CURRENT -->

        <div class="field">

          <label for="current-password">
            Текущий пароль
          </label>


          <div
            class="input-wrapper"
            :class="{
              error:
                showErrors
                && !form.current
            }"
          >

            <input
              id="current-password"
              :type="
                showCurrent
                  ? 'text'
                  : 'password'
              "
              v-model.trim="form.current"
              placeholder="Введите текущий пароль"
            />


            <button
              type="button"
              class="eye-button"
              @click="
                showCurrent =
                  !showCurrent
              "
              tabindex="-1"
            >

              <img
                src="../../assets/eye-icon.png"
                class="eye"
                alt=""
              />

            </button>

          </div>


          <transition name="hint">

            <p
              v-if="
                showErrors
                && !form.current
              "
              class="hint-error"
            >
              Укажите текущий пароль
            </p>

          </transition>

        </div>


        <!-- NEW -->

        <div class="field">

          <div class="field-label-row">

            <label for="new-password">
              Новый пароль
            </label>


            <span
              v-if="form.new"
              class="password-length"
              :class="{
                valid:
                  validNewLen
              }"
            >
              {{ form.new.length }}/8
            </span>

          </div>


          <div
            class="input-wrapper"
            :class="{
              error:
                showErrors
                && (
                  !validNewLen
                  || sameAsCurrent
                ),
              success:
                form.new
                && validNewLen
                && !sameAsCurrent
            }"
          >

            <input
              id="new-password"
              :type="
                showNew
                  ? 'text'
                  : 'password'
              "
              v-model.trim="form.new"
              placeholder="Минимум 8 символов"
            />


            <button
              type="button"
              class="eye-button"
              @click="
                showNew =
                  !showNew
              "
              tabindex="-1"
            >

              <img
                src="../../assets/eye-icon.png"
                class="eye"
                alt=""
              />

            </button>

          </div>


          <!-- PASSWORD STRENGTH -->

          <div
            v-if="form.new"
            class="password-strength"
          >

            <div class="strength-bars">

              <span
                v-for="index in 3"
                :key="index"
                :class="{
                  active:
                    passwordStrength
                    >= index
                }"
              ></span>

            </div>


            <span class="strength-label">
              {{ passwordStrengthLabel }}
            </span>

          </div>


          <transition name="hint">

            <div
              v-if="showErrors"
              class="errors"
            >

              <p
                v-if="!validNewLen"
                class="hint-error"
              >
                Минимум 8 символов
              </p>


              <p
                v-if="sameAsCurrent"
                class="hint-error"
              >
                Новый пароль не должен совпадать с текущим
              </p>

            </div>

          </transition>

        </div>


        <!-- CONFIRM -->

        <div class="field">

          <label for="confirm-password">
            Подтверждение пароля
          </label>


          <div
            class="input-wrapper"
            :class="{
              error:
                showErrors
                && !matches,
              success:
                form.confirm
                && matches
            }"
          >

            <input
              id="confirm-password"
              :type="
                showConfirm
                  ? 'text'
                  : 'password'
              "
              v-model.trim="form.confirm"
              placeholder="Повторите новый пароль"
            />


            <button
              type="button"
              class="eye-button"
              @click="
                showConfirm =
                  !showConfirm
              "
              tabindex="-1"
            >

              <img
                src="../../assets/eye-icon.png"
                class="eye"
                alt=""
              />

            </button>

          </div>


          <transition name="hint">

            <p
              v-if="
                showErrors
                && !matches
              "
              class="hint-error"
            >
              Пароли не совпадают
            </p>

          </transition>

        </div>

      </div>


      <!-- =====================================================
           SAVE
      ====================================================== -->

      <div class="save-area">

        <div class="security-hint">

          <div class="security-hint-icon">

            <svg
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10 2.5L16 5V9.4C16 13.1 13.5 16.1 10 17.5C6.5 16.1 4 13.1 4 9.4V5L10 2.5Z"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linejoin="round"
              />

              <path
                d="M7.5 9.8L9.2 11.5L12.8 7.9"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              />

            </svg>

          </div>

          Пароль хранится в защищённом виде

        </div>


        <button
          type="button"
          class="primary-btn"
          :class="{
            saved:
              justSaved
          }"
          :disabled="
            !isValid
            || isSaving
            || isDisabled
          "
          @click="save"
        >

          <span
            v-if="isSaving"
            class="btn-spinner"
            aria-hidden="true"
          ></span>


          <svg
            v-else-if="justSaved"
            class="saved-icon"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5 10.3L8.2 13.5L15 6.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>


          <span>
            {{ buttonText }}
          </span>

        </button>

      </div>

    </div>

  </section>

</template>


<script setup>

import {
  ref,
  computed
} from "vue";


/*
 * Сейчас функция отключена.
 *
 * Когда подключим backend —
 * достаточно поставить false.
 */

const isDisabled =
  ref(true);


const form = ref({
  current: "",
  new: "",
  confirm: ""
});


const showCurrent =
  ref(false);

const showNew =
  ref(false);

const showConfirm =
  ref(false);


const showErrors =
  ref(false);

const isSaving =
  ref(false);

const justSaved =
  ref(false);


/* ============================================================
   VALIDATION
============================================================ */

const validNewLen =
  computed(
    () =>
      form.value.new.length
      >= 8
  );


const matches =
  computed(
    () =>
      !!form.value.new
      && form.value.new
      === form.value.confirm
  );


const sameAsCurrent =
  computed(
    () =>
      !!form.value.current
      && form.value.current
      === form.value.new
  );


const isValid =
  computed(
    () =>
      !!form.value.current
      && validNewLen.value
      && matches.value
      && !sameAsCurrent.value
  );


/* ============================================================
   PASSWORD STRENGTH
============================================================ */

const passwordStrength =
  computed(() => {

    const value =
      form.value.new;


    if (!value) {
      return 0;
    }


    let score = 0;


    if (value.length >= 8) {
      score += 1;
    }


    if (
      /[A-Za-z]/.test(value)
      && /\d/.test(value)
    ) {
      score += 1;
    }


    if (
      value.length >= 12
      || /[^A-Za-z0-9]/.test(value)
    ) {
      score += 1;
    }


    return score;

  });


const passwordStrengthLabel =
  computed(() => {

    switch (
      passwordStrength.value
    ) {

      case 1:
        return "Слабый";

      case 2:
        return "Хороший";

      case 3:
        return "Надёжный";

      default:
        return "";

    }

  });


/* ============================================================
   BUTTON
============================================================ */

const buttonText =
  computed(() => {

    if (isSaving.value) {
      return "Сохраняем…";
    }


    if (justSaved.value) {
      return "Готово";
    }


    return "Сменить пароль";

  });


/* ============================================================
   SAVE
============================================================ */

async function save() {

  showErrors.value =
    true;


  if (
    !isValid.value
    || isSaving.value
    || isDisabled.value
  ) {
    return;
  }


  isSaving.value =
    true;

  justSaved.value =
    false;


  try {

    /*
     * Здесь позже подключим
     * настоящий backend.
     */

    await new Promise(
      resolve =>
        setTimeout(
          resolve,
          900
        )
    );


    form.value = {
      current: "",
      new: "",
      confirm: ""
    };


    showErrors.value =
      false;


    justSaved.value =
      true;


    setTimeout(
      () => {
        justSaved.value =
          false;
      },
      1200
    );


  } catch (error) {

    console.error(
      "❌ Ошибка смены пароля:",
      error
    );


  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.password-page {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   BASE CARDS
============================================================ */

.password-card,
.form-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.09
    );

  border-radius: 24px;

  box-shadow:
    0 16px 45px
    rgba(
      15,
      23,
      42,
      0.055
    );
}


/* ============================================================
   HEADER CARD
============================================================ */

.password-card {
  padding: 28px;
}


.card-header {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 20px;
}


.header-left {
  display: flex;
  align-items: flex-start;

  gap: 15px;

  min-width: 0;
}


.header-icon {
  width: 48px;
  height: 48px;

  flex:
    0
    0
    48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #447edc;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(124, 92, 231, 0.075)
    );

  border:
    1px solid
    rgba(
      40,
      120,
      230,
      0.11
    );

  border-radius: 15px;
}


.header-icon svg {
  width: 23px;
  height: 23px;
}


.header-copy {
  min-width: 0;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #9499a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.card-title {
  margin: 0;

  color: #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  line-height: 1.3;

  letter-spacing: -0.25px;
}


.card-description {
  max-width: 520px;

  margin:
    6px
    0
    0;

  color: #80858e;

  font-size: 11px;

  line-height: 1.5;
}


/* ============================================================
   GLOW
============================================================ */

.card-glow {
  position: absolute;

  width: 220px;
  height: 220px;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(75px);

  opacity: 0.1;
}


.card-glow-blue {
  top: -120px;
  right: -60px;

  background: #2887f7;
}


.card-glow-purple {
  bottom: -160px;
  left: -100px;

  background: #7c5ce7;
}


/* ============================================================
   AVAILABILITY
============================================================ */

.availability-badge {
  position: relative;

  z-index: 3;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  min-height: 27px;

  padding:
    0
    10px;

  flex-shrink: 0;

  color: #9a772c;

  background:
    rgba(
      237,
      177,
      56,
      0.09
    );

  border:
    1px solid
    rgba(
      220,
      159,
      40,
      0.14
    );

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


.availability-dot {
  width: 5px;
  height: 5px;

  background: #dfaa3d;

  border-radius: 50%;
}


/* ============================================================
   DISABLED NOTICE
============================================================ */

.disabled-notice {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: center;

  gap: 11px;

  margin-top: 22px;

  padding: 13px;

  background:
    rgba(
      238,
      174,
      50,
      0.055
    );

  border:
    1px solid
    rgba(
      221,
      165,
      54,
      0.1
    );

  border-radius: 14px;
}


.disabled-notice-icon {
  width: 34px;
  height: 34px;

  flex:
    0
    0
    34px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #ae8739;

  background:
    rgba(
      219,
      163,
      51,
      0.09
    );

  border-radius: 10px;
}


.disabled-notice-icon svg {
  width: 17px;
  height: 17px;
}


.disabled-notice-title {
  color: #6f6045;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.disabled-notice-description {
  max-width: 520px;

  margin-top: 3px;

  color: #9d917b;

  font-size: 8px;

  line-height: 1.4;
}


/* ============================================================
   FORM CARD
============================================================ */

.form-card {
  padding: 27px;
}


.section-heading {
  margin-bottom: 23px;
}


.section-heading h3 {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.section-heading p {
  margin:
    5px
    0
    0;

  color: #989da5;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   FIELDS
============================================================ */

.fields {
  display: flex;
  flex-direction: column;

  gap: 17px;
}


.field {
  display: flex;
  flex-direction: column;
}


.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 15px;
}


.field label {
  margin-bottom: 7px;

  color: #626873;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.3;
}


.password-length {
  margin-bottom: 7px;

  color: #a2a7af;

  font-size: 8px;
}


.password-length.valid {
  color: #479a6e;
}


/* ============================================================
   INPUT
============================================================ */

.input-wrapper {
  width: 100%;
  min-height: 49px;

  display: flex;
  align-items: center;

  box-sizing: border-box;

  padding:
    0
    9px
    0
    14px;

  background: #f6f8fa;

  border:
    1px solid
    #e1e5ea;

  border-radius: 14px;

  transition:
    background-color
    0.2s ease,
    border-color
    0.2s ease,
    box-shadow
    0.2s ease;
}


.input-wrapper:focus-within {
  background: #ffffff;

  border-color: #5c8ff7;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      92,
      143,
      247,
      0.1
    );
}


.input-wrapper.error {
  border-color:
    rgba(
      220,
      75,
      75,
      0.65
    );

  box-shadow:
    0
    0
    0
    3px
    rgba(
      220,
      75,
      75,
      0.08
    );
}


.input-wrapper.success {
  border-color:
    rgba(
      61,
      174,
      108,
      0.34
    );
}


.input-wrapper input {
  min-width: 0;

  flex: 1;

  padding: 0;

  color: #252a31;

  background: transparent;

  border: none;
  outline: none;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
}


.input-wrapper input::placeholder {
  color: #aaaeb6;
}


/* ============================================================
   EYE
============================================================ */

.eye-button {
  width: 34px;
  height: 34px;

  flex:
    0
    0
    34px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  background: transparent;

  border: none;
  border-radius: 9px;

  cursor: pointer;

  transition:
    background-color
    0.18s ease;
}


.eye-button:hover {
  background:
    rgba(
      40,
      105,
      210,
      0.06
    );
}


.eye {
  width: 17px;
  height: 17px;

  display: block;

  object-fit: contain;

  opacity: 0.45;
}


/* ============================================================
   PASSWORD STRENGTH
============================================================ */

.password-strength {
  display: flex;
  align-items: center;

  gap: 8px;

  margin-top: 7px;
}


.strength-bars {
  width: 105px;

  display: grid;

  grid-template-columns:
    repeat(
      3,
      1fr
    );

  gap: 3px;
}


.strength-bars span {
  height: 3px;

  background: #e3e6ea;

  border-radius: 999px;

  transition:
    background-color
    0.2s ease;
}


.strength-bars span.active {
  background: #7b9cdc;
}


.strength-label {
  color: #9298a1;

  font-size: 8px;
}


/* ============================================================
   ERRORS
============================================================ */

.errors {
  display: flex;
  flex-direction: column;

  gap: 3px;
}


.hint-error {
  margin:
    6px
    0
    0;

  color: #d75b5b;

  font-size: 8px;

  line-height: 1.35;
}


/* ============================================================
   SAVE
============================================================ */

.save-area {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-top: 25px;

  padding-top: 20px;

  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.07
    );
}


.security-hint {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  color: #9297a0;

  font-size: 8px;
}


.security-hint-icon {
  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #718bb6;
}


.security-hint-icon svg {
  width: 17px;
  height: 17px;
}


/* ============================================================
   SAVE BUTTON
============================================================ */

.primary-btn {
  min-width: 145px;
  min-height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    17px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0
    7px
    18px
    rgba(
      40,
      111,
      225,
      0.2
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease,
    box-shadow
    0.2s ease,
    background
    0.2s ease;
}


.primary-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    23px
    rgba(
      40,
      111,
      225,
      0.27
    );
}


.primary-btn:active:not(:disabled) {
  transform:
    translateY(0)
    scale(0.98);
}


.primary-btn:disabled {
  opacity: 0.38;

  cursor: default;

  box-shadow: none;
}


.primary-btn.saved {
  background:
    linear-gradient(
      135deg,
      #39b875,
      #299761
    );
}


.saved-icon {
  width: 15px;
  height: 15px;
}


.btn-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.7s
    linear
    infinite;
}


@keyframes spin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DISABLED STATE
============================================================ */

/*
 * В отличие от старого opacity: .55 на всём блоке,
 * здесь отключаем только форму.
 *
 * Header остаётся нормальным и читаемым.
 */

.password-page.disabled
.form-card {
  opacity: 0.55;

  pointer-events: none;

  user-select: none;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.password-card,
html[data-theme="dark"]
.form-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    18px
    50px
    rgba(
      0,
      0,
      0,
      0.24
    );
}


html[data-theme="dark"]
.card-title,
html[data-theme="dark"]
.section-heading h3 {
  color: #f1f2f4;
}


html[data-theme="dark"]
.card-description,
html[data-theme="dark"]
.section-heading p {
  color: #8e939c;
}


html[data-theme="dark"]
.disabled-notice {
  background:
    rgba(
      211,
      158,
      52,
      0.07
    );

  border-color:
    rgba(
      223,
      173,
      69,
      0.11
    );
}


html[data-theme="dark"]
.disabled-notice-title {
  color: #d6c29c;
}


html[data-theme="dark"]
.disabled-notice-description {
  color: #998f7c;
}


html[data-theme="dark"]
.field label {
  color: #c4c7cd;
}


html[data-theme="dark"]
.input-wrapper {
  background: #292b30;

  border-color: #3c3f46;
}


html[data-theme="dark"]
.input-wrapper:focus-within {
  background: #2e3036;

  border-color: #588bec;

  box-shadow:
    0
    0
    0
    3px
    rgba(
      88,
      139,
      236,
      0.13
    );
}


html[data-theme="dark"]
.input-wrapper input {
  color: #f1f2f4;
}


html[data-theme="dark"]
.input-wrapper input::placeholder {
  color: #727780;
}


html[data-theme="dark"]
.eye {
  filter:
    brightness(0)
    invert(1);

  opacity: 0.47;
}


html[data-theme="dark"]
.eye-button:hover {
  background:
    rgba(
      255,
      255,
      255,
      0.05
    );
}


html[data-theme="dark"]
.strength-bars span {
  background: #393c43;
}


html[data-theme="dark"]
.strength-bars span.active {
  background: #7299e5;
}


html[data-theme="dark"]
.save-area {
  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .password-page {
    gap: 13px;
  }


  .password-card,
  .form-card {
    padding: 20px;

    border-radius: 20px;
  }


  .card-header {
    gap: 15px;
  }


  .header-icon {
    width: 43px;
    height: 43px;

    flex-basis: 43px;
  }


  .availability-badge {
    min-height: 24px;

    padding:
      0
      8px;
  }


  .disabled-notice {
    align-items: flex-start;
  }


  .save-area {
    align-items: stretch;
    flex-direction: column;

    gap: 14px;
  }


  .primary-btn {
    width: 100%;

    min-height: 42px;
  }

}


/* ============================================================
   TRANSITIONS
============================================================ */

.hint-enter-active,
.hint-leave-active {
  transition:
    opacity
    0.16s ease,
    transform
    0.16s ease;
}


.hint-enter-from,
.hint-leave-to {
  opacity: 0;

  transform:
    translateY(-3px);
}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .input-wrapper,
  .eye-button,
  .primary-btn,
  .hint-enter-active,
  .hint-leave-active {
    transition: none;
  }


  .btn-spinner {
    animation: none;
  }

}

</style>